import { gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { Alert } from '@apollo/orbit';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { insightsRouteConfig } from 'src/app/graph/routes';
import {
  HistogramData,
  useQueryHistogramStackInfo,
} from 'src/components/charts/helpers/useQueryHistogramStackInfo';
import { usePrepareHistogramData } from 'src/components/charts/Histogram';
import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { FeatureUnavailable } from 'src/components/planProtectedArea/featureUnavailable/FeatureUnavailable';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import {
  DurationToTraceQuery,
  DurationToTraceQueryVariables,
  QueryTraceHistogramQuery,
  QueryTraceHistogramQueryVariables,
  QueryTraceQuery,
  QueryTraceQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { HistogramTraceFinder } from './histogramTraceFinder/HistogramTraceFinder';
import { traceFragment } from './histogramTraceFinder/queryTraceSection/trace/TraceTree';
import { queryTraceSummaryCardFragment } from './histogramTraceFinder/queryTraceSummaryCard/QueryTraceSummaryCard';

export const histogramQuery = gql<
  QueryTraceHistogramQuery,
  QueryTraceHistogramQueryVariables
>`
  query QueryTraceHistogramQuery(
    $serviceId: ID!
    $histogramFilter: ServiceQueryStatsFilter
    $traceRefsFilter: ServiceTraceRefsFilter
    $noFilter: Boolean!
    $from: Timestamp!
    $to: Timestamp!
  ) {
    service(id: $serviceId) {
      id
      stats: statsWindow(from: $from, to: $to) {
        queryStats(filter: $histogramFilter) @skip(if: $noFilter) {
          timestamp
          metrics {
            uncachedHistogram {
              sparseBuckets
            }
            cachedHistogram {
              sparseBuckets
            }
          }
        }
        traceRefs(filter: $traceRefsFilter) @skip(if: $noFilter) {
          group: groupBy {
            durationBucket
          }
        }
      }
    }
  }
`;

export const durationToTraceQuery = gql<
  DurationToTraceQuery,
  DurationToTraceQueryVariables
>`
  query DurationToTraceQuery(
    $serviceId: ID!
    $filter: ServiceTraceRefsFilter
    $noFilter: Boolean!
    $from: Timestamp!
    $to: Timestamp!
  ) {
    service(id: $serviceId) {
      id
      stats: statsWindow(from: $from, to: $to) {
        traceRefs(filter: $filter, limit: 1) @skip(if: $noFilter) {
          group: groupBy {
            generatedTraceId
            durationBucket
          }
        }
      }
    }
  }
`;

export const traceQuery = gql<QueryTraceQuery, QueryTraceQueryVariables>`
  query QueryTraceQuery($serviceId: ID!, $traceId: ID!) {
    service(id: $serviceId) {
      id
      trace(id: $traceId) {
        id
        ...TraceFragment
        ...QueryTraceSummaryCardFragment
      }
    }
  }
  ${traceFragment}
  ${queryTraceSummaryCardFragment}
`;

function TraceGranularityWarningBanner({
  range,
  from,
  to,
}: {
  range: TimeRangeOption;
  from: string | undefined;
  to: string | undefined;
}) {
  // Track custom event for the cases where the warning banner is shown and when
  // it would have been shown if the LaunchDarkly flag was enabled
  const trackCustomEvent = useTrackCustomEvent();

  // No need to show the warning if:
  // - we're not using a custom time range
  // - we're not yet past the 14 day rollup threshold
  // - our time range is greater than an hour (we assume they're not actually
  // looking for sub-hour granularity)
  // - the time range crosses an hour boundary
  const fromMoment = moment(from);
  const toMoment = moment(to);
  const bannerTimeRange =
    range === 'custom' &&
    moment().diff(fromMoment, 'days') >= 14 &&
    toMoment.diff(fromMoment, 'hours') < 1 &&
    fromMoment.minutes() < toMoment.minutes();

  if (!bannerTimeRange) {
    return null;
  }

  trackCustomEvent({
    category: 'Trace Rollup',
    action: 'trace_granularity_warning_banner',
    label: 'Banner shown',
  });

  return (
    <Alert status="warning" className="mb-4">
      To find traces that are more than 14 days old, your custom time range must
      include the start of the hour on which the trace was recorded. To see
      traces that occurred between {fromMoment.format('h:mm a')} and{' '}
      {fromMoment.clone().add(1, 'hour').startOf('hour').format('h:mm a')}, your
      custom time range must start at{' '}
      {fromMoment.startOf('hour').format('h:mm a')}.
    </Alert>
  );
}

type Props = {
  serviceId: string;
  schemaTag: string;
  range: TimeRangeOption;
  queryId: string;
  traceId: string | undefined;
  isTracingAvailable: boolean;
  currentBucket: number | null;
};

function QueryTracesContent(
  props: Omit<
    React.ComponentProps<typeof HistogramTraceFinder>,
    'histogramData'
  > & { data: HistogramData[] | undefined },
) {
  const histogramStackInfo = useQueryHistogramStackInfo(props.data);
  const data = usePrepareHistogramData({
    ...histogramStackInfo,
    durationBuckets: props.durationBuckets,
  });
  const histogramData = useMemo(
    () => ({ ...histogramStackInfo, data }),
    [histogramStackInfo, data],
  );
  return <HistogramTraceFinder histogramData={histogramData} {...props} />;
}
export const QueryTraces = ({
  range,
  serviceId,
  schemaTag,
  queryId,
  traceId,
  currentBucket,
  isTracingAvailable,
}: Props) => {
  const { to, from } = useRouteParams(insightsRouteConfig);
  const isInBackground = useIsAppInBackgroundContext();
  const { timeFrom, timeTo, pollInterval } = date.getDateBounds(range, {
    from,
    to,
  });

  const location = useLocation();
  const history = useHistory();
  const setTraceBucketTo = useCallback(
    (traceBucket: number) => {
      history.push(
        insightsRouteConfig.locationFrom({
          location,
          patch: {
            [Config.queryParameters.ActiveReportPaneTab]: 'traces',
            [Config.queryParameters.SelectedDurationBucket]:
              String(traceBucket),
            [Config.queryParameters.SelectedTraceId]: RouteConfig.DELETE,
          },
          fromRouteConfig: insightsRouteConfig,
        }),
      );
    },
    [history, location],
  );

  if (!isTracingAvailable) return <FeatureUnavailable />;

  return (
    <>
      <TraceGranularityWarningBanner
        range={range}
        from={timeFrom}
        to={timeTo}
      />
      <FilterProvider>
        {({ filter }) => (
          <Query<QueryTraceHistogramQuery, QueryTraceHistogramQueryVariables>
            query={histogramQuery}
            variables={{
              serviceId,
              from: timeFrom,
              to: timeTo,
              histogramFilter: { queryId, ...filter },
              traceRefsFilter: { queryId, ...filter },
              noFilter: !filter,
            }}
            pollInterval={pollInterval}
            skip={isInBackground}
            skipPollAttempt={() => isInBackground}
          >
            {({
              data: histogramData,
              loading: histogramLoading,
              error: histogramError,
            }) => {
              if (histogramError) {
                console.log(histogramError); // eslint-disable-line no-console
              }

              const durationBuckets = (
                histogramData?.service?.stats?.traceRefs ?? []
              )
                .map(({ group }) => group && group.durationBucket)
                .filter(
                  (duration): duration is number =>
                    typeof duration === 'number',
                )
                .sort();

              return (
                <Query<DurationToTraceQuery, DurationToTraceQueryVariables>
                  query={durationToTraceQuery}
                  variables={{
                    serviceId,
                    filter: {
                      queryId,
                      durationBucket: currentBucket,
                      ...filter,
                    },
                    noFilter: !filter,
                    from: timeFrom,
                    to: timeTo,
                  }}
                  skip={Boolean(traceId) || isInBackground}
                  skipPollAttempt={() => Boolean(traceId) || isInBackground}
                >
                  {({
                    data: bucketToTraceData,
                    loading: bucketToTraceLoading,
                  }) => {
                    const chosenTraceId =
                      traceId ||
                      bucketToTraceData?.service?.stats?.traceRefs?.[0]?.group
                        .generatedTraceId;

                    const skip =
                      !chosenTraceId || bucketToTraceLoading || isInBackground;
                    return (
                      <Query<QueryTraceQuery, QueryTraceQueryVariables>
                        query={traceQuery}
                        variables={{
                          serviceId,
                          traceId: chosenTraceId ?? '', // This is safe because the query is skipped if it's falsy
                        }}
                        skip={skip}
                        skipPollAttempt={() => skip}
                      >
                        {({
                          data: traceData,
                          loading: traceLoading,
                          error: traceError,
                        }) => {
                          if (traceError) {
                            console.log(traceError); // eslint-disable-line no-console
                          }
                          const trace =
                            (traceData &&
                              traceData.service &&
                              traceData.service.trace) ||
                            null;
                          return (
                            <QueryTracesContent
                              trace={trace}
                              traceLoading={
                                bucketToTraceLoading || traceLoading
                              }
                              requestedBucket={currentBucket}
                              serviceId={serviceId}
                              schemaTag={schemaTag}
                              durationBuckets={durationBuckets}
                              queryParamsTraceId={traceId}
                              histogramLoading={histogramLoading}
                              setTraceBucketTo={setTraceBucketTo}
                              data={
                                histogramData?.service?.stats?.queryStats ??
                                undefined
                              }
                            />
                          );
                        }}
                      </Query>
                    );
                  }}
                </Query>
              );
            }}
          </Query>
        )}
      </FilterProvider>
    </>
  );
};
